import { formatTimestamp } from "../lib/time";
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { AlertCircle, Copy, KeyRound, Loader2, Plus, RefreshCw, Trash2 } from 'lucide-react';

import { Sidebar } from '../components_dashboard/Sidebar';
import { Alert, AlertDescription, AlertTitle } from '../components_dashboard/ui/alert';
import { Badge } from '../components_dashboard/ui/badge';
import { Button } from '../components_dashboard/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components_dashboard/ui/card';
import { toast } from '../components/CustomToast';
import { useAuth } from '../context/AuthContext';

const apiUrl = process.env.REACT_APP_BACKEND_URL;

function formatDateTime(value, timeZone) {
  if (!value) return 'Never';
  return formatTimestamp(value, timeZone);
}

export default function DeveloperApiKeysDashboard() {
  const { token, user } = useAuth();
  const [apiKeys, setApiKeys] = useState([]);
  const [activeCount, setActiveCount] = useState(0);
  const [maxActiveKeys, setMaxActiveKeys] = useState(3);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [creating, setCreating] = useState(false);
  const [deletingKeyId, setDeletingKeyId] = useState('');
  const [generatedKey, setGeneratedKey] = useState('');

  const remainingSlots = useMemo(() => Math.max(0, maxActiveKeys - activeCount), [activeCount, maxActiveKeys]);
  const hasHitLimit = activeCount >= maxActiveKeys;

  const fetchApiKeys = useCallback(async ({ silent = false } = {}) => {
    if (!token) return;
    if (silent) {
      setRefreshing(true);
    } else {
      setLoading(true);
    }
    try {
      const response = await axios.get(`${apiUrl}/api/v1/public/api-key`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = response.data?.data || {};
      setApiKeys(Array.isArray(data.api_keys) ? data.api_keys : []);
      setActiveCount(Number(data.active_count || 0));
      setMaxActiveKeys(Number(data.max_active_keys || 3));
    } catch (error) {
      console.error('Failed to load developer API keys', error);
      toast.error(error.response?.data?.detail || 'Failed to load developer API keys.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [token]);

  useEffect(() => {
    void fetchApiKeys();
  }, [fetchApiKeys]);

  const createApiKey = async () => {
    if (!token) return;
    setCreating(true);
    try {
      const response = await axios.post(
        `${apiUrl}/api/v1/public/api-key`,
        {},
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const payload = response.data?.data || {};
      if (payload.api_key) {
        setGeneratedKey(payload.api_key);
      }
      toast.success('Developer API key created. Save it now; it will not be shown again.');
      await fetchApiKeys({ silent: true });
    } catch (error) {
      console.error('Failed to create developer API key', error);
      toast.error(error.response?.data?.detail || 'Failed to create developer API key.');
    } finally {
      setCreating(false);
    }
  };

  const deleteApiKey = async (apiKeyId) => {
    if (!token) return;
    const confirmed = window.confirm('Delete this developer API key? You can create a new one after deleting an existing key.');
    if (!confirmed) return;
    setDeletingKeyId(apiKeyId);
    try {
      await axios.delete(`${apiUrl}/api/v1/public/api-key/${apiKeyId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Developer API key deleted.');
      await fetchApiKeys({ silent: true });
    } catch (error) {
      console.error('Failed to delete developer API key', error);
      toast.error(error.response?.data?.detail || 'Failed to delete developer API key.');
    } finally {
      setDeletingKeyId('');
    }
  };

  const copyToClipboard = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
      toast.success('Copied to clipboard.');
    } catch (error) {
      console.error('Failed to copy developer API key', error);
      toast.error('Could not copy to clipboard.');
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50">
      <Sidebar />
      <main className="flex-1 min-h-screen p-6 lg:ml-60 lg:p-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 mb-3 text-xs font-medium text-indigo-700 bg-indigo-50 rounded-full">
                <KeyRound className="w-3.5 h-3.5" />
                Developer Integrations
              </div>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-900">Manage Developer API Keys</h1>
              <p className="mt-2 text-sm text-slate-600">
                Create up to 3 active API keys for this account. If the limit is reached, delete an existing key before creating a new one.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              <Button variant="outline" onClick={() => fetchApiKeys({ silent: true })} disabled={refreshing || loading} className="border-slate-200">
                {refreshing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                Refresh
              </Button>
              <Button onClick={createApiKey} disabled={creating || hasHitLimit} className="bg-slate-900 text-white hover:bg-slate-800">
                {creating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Create API Key
              </Button>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardDescription>Active keys</CardDescription>
                <CardTitle className="text-3xl">{activeCount}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardDescription>Maximum keys</CardDescription>
                <CardTitle className="text-3xl">{maxActiveKeys}</CardTitle>
              </CardHeader>
            </Card>
            <Card className="border-slate-200 shadow-sm">
              <CardHeader className="pb-3">
                <CardDescription>Remaining slots</CardDescription>
                <CardTitle className="text-3xl">{remainingSlots}</CardTitle>
              </CardHeader>
            </Card>
          </div>

          <Alert className="border-indigo-200 bg-indigo-50 text-indigo-900">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Key secret is shown only once</AlertTitle>
            <AlertDescription>
              Save a newly generated secret immediately. After that, only the prefix and metadata are stored here.
            </AlertDescription>
          </Alert>

          {generatedKey ? (
            <Card className="border-emerald-200 bg-emerald-50/60 shadow-sm">
              <CardHeader>
                <CardTitle className="text-lg text-emerald-950">New API Key Generated</CardTitle>
                <CardDescription className="text-emerald-900/80">Copy this secret now. It will not be visible again.</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex flex-col gap-3 rounded-xl border border-emerald-200 bg-white p-4 shadow-sm md:flex-row md:items-center md:justify-between">
                  <div className="min-w-0">
                    <div className="text-xs font-medium uppercase tracking-wide text-emerald-700">API Secret</div>
                    <div className="mt-1 break-all font-mono text-sm text-slate-900">{generatedKey}</div>
                  </div>
                  <Button onClick={() => copyToClipboard(generatedKey)} className="bg-emerald-600 text-white hover:bg-emerald-700">
                    <Copy className="w-4 h-4 mr-2" />
                    Copy secret
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          <Card className="border-slate-200 shadow-sm">
            <CardHeader className="flex flex-row items-start justify-between gap-4">
              <div>
                <CardTitle>Existing API Keys</CardTitle>
                <CardDescription>Use the delete action to retire a key before generating a replacement.</CardDescription>
              </div>
              <Badge variant={hasHitLimit ? 'destructive' : 'outline'} className="h-fit px-3 py-1">
                {hasHitLimit ? 'Limit reached' : `${remainingSlots} slot${remainingSlots === 1 ? '' : 's'} available`}
              </Badge>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex items-center justify-center py-14 text-slate-500">
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Loading API keys...
                </div>
              ) : apiKeys.length ? (
                <div className="space-y-3">
                  {apiKeys.map((apiKey) => (
                    <div key={apiKey.api_key_id} className="flex flex-col gap-4 rounded-xl border border-slate-200 bg-white p-4 shadow-sm lg:flex-row lg:items-center lg:justify-between">
                      <div className="min-w-0 space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-medium text-slate-900">{apiKey.name || 'Developer API Key'}</h3>
                          <Badge variant="outline">{apiKey.is_active ? 'Active' : 'Inactive'}</Badge>
                        </div>
                        <div className="text-sm text-slate-600">ID: {apiKey.api_key_id}</div>
                        <div className="text-sm text-slate-600">Created: {formatDateTime(apiKey.created_at, user?.timezone)}</div>
                        <div className="text-sm text-slate-600">Last used: {formatDateTime(apiKey.last_used_at, user?.timezone)}</div>
                        <div className="pt-1 text-sm text-slate-500">
                          Prefix: <span className="font-mono text-slate-700">{apiKey.key_prefix}</span>
                        </div>
                      </div>
                      <div className="flex flex-wrap gap-2 lg:justify-end">
                        <Button variant="outline" className="border-slate-200" onClick={() => copyToClipboard(apiKey.key_prefix)}>
                          <Copy className="w-4 h-4 mr-2" />
                          Copy prefix
                        </Button>
                        <Button
                          variant="destructive"
                          onClick={() => deleteApiKey(apiKey.api_key_id)}
                          disabled={deletingKeyId === apiKey.api_key_id}
                        >
                          {deletingKeyId === apiKey.api_key_id ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Trash2 className="w-4 h-4 mr-2" />}
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-slate-200 bg-slate-50 px-6 py-16 text-center">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-slate-900 text-white">
                    <KeyRound className="h-5 w-5" />
                  </div>
                  <div>
                    <div className="text-lg font-medium text-slate-900">No API keys yet</div>
                    <div className="mt-1 text-sm text-slate-600">Create the first developer API key to start calling the public developer endpoints.</div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="text-xs text-slate-500">Signed in as {user?.email || 'unknown user'}</div>
        </div>
      </main>
    </div>
  );
}